import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button, H2, Input, Select, CountryFlag } from "@/shared/ui";
import {
  ROUTES,
  formatDate,
  generatePDF,
  downloadPDF,
  getCountryName,
} from "@/shared/core";
import { getDocument, updateDocument, deleteDocument } from "@/shared/db";
import { improveDocument } from "@/shared/ai";
import type { CountryCode } from "@/shared/config/country-legal-context";
import { useConfirm } from "../../../hooks/useConfirm";
import { toast } from "sonner";
import { analytics } from "../../../utils/analytics";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import {
  Download,
  Edit,
  Save,
  X,
  Copy,
  Sparkles,
  Trash2,
  Wand2,
  FileText,
  Scale,
  Zap,
  AlertCircle,
} from "lucide-react";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { markdownToHtml, htmlToMarkdown } from "../../../utils/markdownToHtml";
import type { Document } from "../types/document.types";

export const DocumentViewPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirm, Modal } = useConfirm();
  const { user, profile } = useAuth();
  const { subscription } = usePlanLimits();

  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedStatus, setEditedStatus] = useState<"draft" | "final">("draft");
  const [isSaving, setIsSaving] = useState(false);
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [improveInstructions, setImproveInstructions] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("edit") === "1") {
      setIsEditing(true);
    }
  }, [searchParams]);

  const loadDocument = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const doc = await getDocument(id);
      setDocument(doc as Document);
      setEditedTitle(doc.title);
      setEditedStatus(
        doc.status === "draft" || doc.status === "final"
          ? doc.status
          : "draft"
      );
      // Convertir markdown a HTML para el editor
      setEditedContent(markdownToHtml(doc.content));
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error loading document:", err);
      toast.error(t("documentView.error.loading"));
      navigate(ROUTES.DASHBOARD);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!document) return;

    try {
      const isFreeUser =
        profile?.plan === "free" ||
        subscription?.planType === "lawyer_free";
      const pdf = generatePDF({
        title: document.title,
        content: document.content,
        country: getCountryName(document.country),
        date: formatDate(document.created_at),
        showBranding: isFreeUser,
      });
      downloadPDF(pdf, document.title);

      analytics.track("document_downloaded", {
        document_id: document.id,
        document_type: document.document_type,
        user_id: user?.id,
      });

      toast.success(t("documentView.success.download"));
    } catch (error) {
      analytics.track("document_download_error", {
        document_id: document.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      toast.error(t("documentView.error.download"));
    }
  };

  const handleCopy = () => {
    if (!document) return;

    navigator.clipboard.writeText(document.content);

    analytics.track("document_copied", {
      document_id: document.id,
      user_id: user?.id,
    });

    toast.success(t("documentView.success.copy"));
  };

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    try {
      // Convertir HTML a markdown/texto plano para guardar
      const contentToSave = htmlToMarkdown(editedContent);
      const updated = await updateDocument(id, {
        title: editedTitle,
        content: contentToSave,
        status: editedStatus,
      });
      setDocument(updated as Document);
      setEditedContent(markdownToHtml(updated.content));
      setIsEditing(false);
      setHasUnsavedChanges(false);

      analytics.track("document_saved", {
        document_id: id,
        user_id: user?.id,
      });

      toast.success(t("documentView.success.update"));
    } catch (error) {
      analytics.track("document_save_error", {
        document_id: id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      toast.error(t("documentView.error.update"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!document) return;

    if (hasUnsavedChanges) {
      try {
        const shouldCancel = await confirm({
          title: t("documentView.unsavedChangesTitle"),
          message: t("documentView.unsavedChanges"),
          confirmText: t("common.discard"),
          cancelText: t("common.cancel"),
          variant: "warning",
        });
        if (!shouldCancel) return;
      } catch (error) {
        console.error("Error in confirm:", error);
        return;
      }
    }

    setEditedTitle(document.title);
    setEditedContent(markdownToHtml(document.content));
    setEditedStatus(
      document.status === "draft" || document.status === "final"
        ? document.status
        : "draft"
    );
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  // Detectar cambios no guardados
  useEffect(() => {
    if (!document || !isEditing) return;
    const originalContent = markdownToHtml(document.content);
    const hasChanges =
      editedTitle !== document.title ||
      editedContent !== originalContent ||
      editedStatus !== document.status;
    setHasUnsavedChanges(hasChanges);
  }, [editedTitle, editedContent, editedStatus, document, isEditing]);

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S o Cmd+S para guardar
      if ((e.ctrlKey || e.metaKey) && e.key === "s" && isEditing) {
        e.preventDefault();
        e.stopPropagation();
        if (hasUnsavedChanges) {
          handleSave();
        }
      }
      // Escape para cancelar edición
      if (e.key === "Escape" && isEditing && !showImproveModal) {
        e.preventDefault();
        e.stopPropagation();
        handleCancelEdit();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isEditing, hasUnsavedChanges, showImproveModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImproveWithAI = async (
    withInstructions: boolean = false,
    template?: string
  ) => {
    if (!document || !isEditing) {
      toast.error(t("documentView.aiImprove.error.notEditing"));
      return;
    }

    setIsImproving(true);
    try {
      const contentToImprove = htmlToMarkdown(editedContent);
      let instructions = "";

      if (template) {
        instructions = template;
      } else if (withInstructions) {
        instructions = improveInstructions.trim();
      }

      const improvedContent = await improveDocument({
        documentContent: contentToImprove,
        country: document.country as CountryCode,
        instructions: instructions || undefined,
      });

      setEditedContent(markdownToHtml(improvedContent));
      setShowImproveModal(false);
      setImproveInstructions("");
      setSelectedTemplate(null);
      setHasUnsavedChanges(true);
      toast.success(t("documentView.aiImprove.success"));
    } catch (error) {
      console.error("Error improving document:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("documentView.aiImprove.error.generic");
      toast.error(errorMessage);
    } finally {
      setIsImproving(false);
    }
  };

  const aiTemplates = [
    {
      id: "formal",
      title: t("documentView.aiImprove.templates.formal.title"),
      description: t("documentView.aiImprove.templates.formal.description"),
      icon: FileText,
      instruction: t("documentView.aiImprove.templates.formal.instruction"),
    },
    {
      id: "simplify",
      title: t("documentView.aiImprove.templates.simplify.title"),
      description: t("documentView.aiImprove.templates.simplify.description"),
      icon: Zap,
      instruction: t("documentView.aiImprove.templates.simplify.instruction"),
    },
    {
      id: "compliance",
      title: t("documentView.aiImprove.templates.compliance.title"),
      description: t("documentView.aiImprove.templates.compliance.description"),
      icon: Scale,
      instruction: t("documentView.aiImprove.templates.compliance.instruction"),
    },
  ];

  const handleDelete = async () => {
    if (!id || !document) return;

    await confirm({
      title: t("documentView.delete.title"),
      message: t("documentView.delete.confirm", { title: document.title }),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
      onConfirm: async () => {
        try {
          await deleteDocument(id);

          analytics.track("document_deleted", {
            document_id: id,
            document_type: document.document_type,
            user_id: user?.id,
          });

          toast.success(t("documentView.delete.success"));
          navigate(ROUTES.DASHBOARD);
        } catch (error) {
          console.error("Error deleting document:", error);
          analytics.track("document_delete_error", {
            document_id: id,
            error: error instanceof Error ? error.message : "Unknown error",
          });
          toast.error(t("documentView.delete.error"));
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-nieve flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500 mx-auto mb-4"></div>
          <p className="text-titan">{t("documentView.loading")}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-nieve flex items-center justify-center">
        <div className="text-center">
          <H2 className="mb-4">{t("documentView.notFound")}</H2>
          <Button onClick={() => navigate(ROUTES.DASHBOARD)}>
            {t("documentView.backToDashboard")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="mx-auto w-full max-w-full px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Header - Responsive: stacked on mobile, row on lg+ */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6 md:mb-8 bg-white rounded-xl shadow-md p-4 sm:p-5 md:p-6 border border-tiber-600"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
            {/* Title / Input area */}
            <div className="flex-1 min-w-0 w-full">
              {isEditing ? (
                <Input
                  value={editedTitle}
                  onChange={(e) => {
                    setEditedTitle(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold w-full"
                  placeholder={t(
                    "documentView.titlePlaceholder",
                    "Título del documento"
                  )}
                />
              ) : (
                <H2 className="text-xl sm:text-2xl md:text-3xl font-bold text-tiber-900 mb-2 wrap-break-word">
                  {document.title}
                </H2>
              )}
              {!isEditing && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-sm text-titan">
                  <span className="flex items-center gap-2">
                    <CountryFlag
                      countryCode={document.country}
                      size="1.2em"
                      title={getCountryName(document.country)}
                    />
                    <span className="font-medium">
                      {getCountryName(document.country)}
                    </span>
                  </span>
                  <span className="hidden sm:inline text-tiber-300">•</span>
                  <span>{formatDate(document.created_at)}</span>
                  <span className="hidden sm:inline text-tiber-300">•</span>
                  <span className="capitalize px-2 py-1 bg-tiber-100 text-tiber-700 rounded-md text-xs font-medium">
                    {document.status}
                  </span>
                  <span className="hidden sm:inline text-tiber-300">•</span>
                  <span className="px-2 py-1 bg-tiber-100 text-tiber-700 rounded-md text-xs font-medium">
                    {document.document_type}
                  </span>
                </div>
              )}
            </div>

            {/* Toolbar: Select, indicator, buttons - responsive layout */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 lg:shrink-0">
              {isEditing ? (
                <>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <Select
                      value={editedStatus}
                      onChange={(e) => {
                        setEditedStatus(e.target.value as "draft" | "final");
                        setHasUnsavedChanges(true);
                      }}
                      className="w-full sm:w-36 min-w-0"
                      options={[
                        {
                          value: "draft",
                          label: t("documentView.status.draft", "Borrador"),
                        },
                        {
                          value: "final",
                          label: t("documentView.status.final", "Final"),
                        },
                      ]}
                    />
                    {hasUnsavedChanges && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2 px-3 py-2 sm:py-1.5 bg-dorado/10 border border-dorado/30 rounded-lg text-dorado-dark text-sm min-h-[44px] sm:min-h-0 shrink-0"
                      >
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span className="truncate">
                          {t(
                            "documentView.unsavedChangesIndicator",
                            "Cambios sin guardar"
                          )}
                        </span>
                      </motion.div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => setShowImproveModal(true)}
                      disabled={isImproving}
                      className="bg-linear-to-r from-tiber-50 to-tiber-500/10 hover:from-tiber-100 hover:to-tiber-500/20 border border-tiber-200 min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center"
                    >
                      <Sparkles className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                      <span className="truncate">{t("documentView.aiImprove.button")}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={(e) => handleCancelEdit(e)}
                      type="button"
                      className="min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center"
                    >
                      <X className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                      <span className="truncate">{t("common.cancel")}</span>
                    </Button>
                    <Button
                      onClick={handleSave}
                      isLoading={isSaving}
                      disabled={!hasUnsavedChanges && !isSaving}
                      className="min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center col-span-2 sm:col-span-1"
                    >
                      <Save className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                      <span className="truncate">{t("common.save")}</span>
                    </Button>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleCopy}
                    className="min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center"
                  >
                    <Copy className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                    <span className="truncate">{t("documentView.actions.copy")}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    className="min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center"
                  >
                    <Edit className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                    <span className="truncate">{t("common.edit")}</span>
                  </Button>
                  <Button
                    onClick={handleDownload}
                    className="min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center"
                  >
                    <Download className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                    <span className="truncate">{t("documentView.actions.downloadPDF")}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDelete}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px] sm:min-h-10 text-sm sm:text-base justify-center col-span-2 sm:col-span-1"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5 sm:mr-2 shrink-0" />
                    <span className="truncate">{t("common.delete")}</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
        {/* Document Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 border border-tiber-600 overflow-x-auto"
        >
          {isEditing ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-tiber-200">
                <div className="flex items-center gap-2 text-sm text-tiber-600">
                  <FileText className="w-4 h-4 shrink-0" />
                  <span>{t("documentView.editor.mode")}</span>
                </div>
                <div className="text-xs text-tiber-500 sm:text-right">
                  {t("documentView.editor.tip")}
                </div>
              </div>
              <RichTextEditor
                value={editedContent}
                onChange={(value) => {
                  setEditedContent(value);
                  setHasUnsavedChanges(true);
                }}
                placeholder={t("documentView.editor.placeholder")}
              />
            </div>
          ) : (
            <div
              className="prose max-w-none document-content"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(document.content),
              }}
            />
          )}
        </motion.div>
        <style>{`
          .document-content {
            line-height: 1.8;
            color: #1f2937;
          }
          .document-content h1 {
            font-size: 2em;
            font-weight: bold;
            margin-top: 1em;
            margin-bottom: 0.5em;
          }
          .document-content h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin-top: 0.83em;
            margin-bottom: 0.5em;
          }
          .document-content h3 {
            font-size: 1.17em;
            font-weight: bold;
            margin-top: 1em;
            margin-bottom: 0.5em;
          }
          .document-content p {
            margin: 1em 0;
          }
          .document-content strong {
            font-weight: bold;
          }
          .document-content em {
            font-style: italic;
          }
          .document-content ul, .document-content ol {
            margin: 1em 0;
            padding-left: 2em;
          }
          .document-content li {
            margin: 0.5em 0;
          }
          .document-content blockquote {
            border-left: 4px solid #0f1f3d;
            padding-left: 1em;
            margin: 1em 0;
            color: #6b7280;
          }
          .document-content code {
            background: #f3f4f6;
            padding: 0.2em 0.4em;
            border-radius: 3px;
            font-family: monospace;
          }
        `}</style>

        {/* Metadata */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6 bg-white rounded-xl shadow-md p-6 border border-tiber-600"
        >
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-tiber-600" />
            <h3 className="text-lg font-semibold text-tiber-900">
              {t("documentView.metadata.title")}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-tiber-50 rounded-lg">
              <span className="text-xs text-titan uppercase tracking-wide block mb-1">
                {t("documentView.metadata.type")}
              </span>
              <span className="text-sm font-semibold text-tiber-900">
                {document.document_type}
              </span>
            </div>
            <div className="p-3 bg-tiber-50 rounded-lg">
              <span className="text-xs text-titan uppercase tracking-wide block mb-1">
                {t("documentView.metadata.country")}
              </span>
              <span className="text-sm font-semibold text-tiber-900 flex items-center gap-2">
                <CountryFlag
                  countryCode={document.country}
                  size="1.2em"
                  title={getCountryName(document.country)}
                />
                {getCountryName(document.country)}
              </span>
            </div>
            <div className="p-3 bg-tiber-50 rounded-lg">
              <span className="text-xs text-titan uppercase tracking-wide block mb-1">
                {t("documentView.metadata.created")}
              </span>
              <span className="text-sm font-semibold text-tiber-900">
                {formatDate(document.created_at, "dd/MM/yyyy HH:mm")}
              </span>
            </div>
            <div className="p-3 bg-tiber-50 rounded-lg">
              <span className="text-xs text-titan uppercase tracking-wide block mb-1">
                {t("documentView.metadata.updated")}
              </span>
              <span className="text-sm font-semibold text-tiber-900">
                {formatDate(document.updated_at, "dd/MM/yyyy HH:mm")}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Improve with AI Modal - Mejorado */}
        <AnimatePresence>
          {showImproveModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget && !isImproving) {
                  setShowImproveModal(false);
                  setImproveInstructions("");
                  setSelectedTemplate(null);
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white rounded-3xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto border-2 border-tiber-900"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-linear-to-br from-tiber-500 to-tiber-500 rounded-xl">
                      <Wand2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-tiber-900">
                        {t("documentView.aiImprove.modal.title")}
                      </h3>
                      <p className="text-sm text-tiber-600 mt-1">
                        {t("documentView.aiImprove.modal.description")}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowImproveModal(false);
                      setImproveInstructions("");
                      setSelectedTemplate(null);
                    }}
                    className="text-titan hover:text-tiber-900 transition-colors p-2 hover:bg-romance-50 rounded-lg"
                    disabled={isImproving}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Progress Indicator */}
                {isImproving && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6 p-4 bg-linear-to-r from-tiber-50 to-tiber-500/10 rounded-xl border border-tiber-200"
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <Sparkles className="w-5 h-5 text-tiber-600" />
                      </motion.div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-juridico">
                          {t(
                            "documentView.aiImprove.processing",
                            "Mejorando documento con IA..."
                          )}
                        </p>
                        <p className="text-xs text-tiber-700 mt-1">
                          {t(
                            "documentView.aiImprove.processingDesc",
                            "Esto puede tomar unos momentos"
                          )}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* AI Templates */}
                {!isImproving && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-tiber-700 mb-3">
                      {t(
                        "documentView.aiImprove.templates.title",
                        "Mejoras rápidas"
                      )}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {aiTemplates.map((template) => {
                        const Icon = template.icon;
                        const isSelected = selectedTemplate === template.id;
                        return (
                          <motion.button
                            key={template.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setSelectedTemplate(template.id);
                              setImproveInstructions(template.instruction);
                            }}
                            className={`p-4 rounded-xl border-2 transition-all text-left ${
                              isSelected
                                ? "border-tiber-500 bg-tiber-50 shadow-md"
                                : "border-tiber-200 hover:border-tiber-300 hover:bg-tiber-50/50"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`p-2 rounded-lg ${
                                  isSelected
                                    ? "bg-tiber-500 text-white"
                                    : "bg-tiber-100 text-tiber-700"
                                }`}
                              >
                                <Icon className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-tiber-900 text-sm mb-1">
                                  {template.title}
                                </h4>
                                <p className="text-xs text-tiber-600">
                                  {template.description}
                                </p>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Custom Instructions */}
                {!isImproving && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-tiber-700 mb-2">
                      {t("documentView.aiImprove.modal.instructionsLabel")}{" "}
                      <span className="text-tiber-500 font-normal">
                        ({t("documentView.aiImprove.modal.optional")})
                      </span>
                    </label>
                    <textarea
                      value={improveInstructions}
                      onChange={(e) => {
                        setImproveInstructions(e.target.value);
                        if (selectedTemplate) setSelectedTemplate(null);
                      }}
                      placeholder={t(
                        "documentView.aiImprove.modal.instructionsPlaceholder"
                      )}
                      className="w-full px-4 py-3 border-2 border-tiber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 resize-none transition-all"
                      rows={4}
                    />
                  </div>
                )}

                {/* Actions */}
                {!isImproving && (
                  <div className="flex items-center gap-3 justify-end pt-4 border-t border-tiber-200">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowImproveModal(false);
                        setImproveInstructions("");
                        setSelectedTemplate(null);
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onClick={() => handleImproveWithAI(false)}
                      className="bg-linear-to-r from-tiber-600 to-tiber-500 hover:from-tiber-700 hover:to-tiber-600 text-white"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t("documentView.aiImprove.modal.improveAuto")}
                    </Button>
                    {(improveInstructions.trim() || selectedTemplate) && (
                      <Button
                        onClick={() => {
                          const template = selectedTemplate
                            ? aiTemplates.find((t) => t.id === selectedTemplate)
                                ?.instruction
                            : undefined;
                          handleImproveWithAI(
                            !!improveInstructions.trim(),
                            template
                          );
                        }}
                        variant="primary"
                        className="bg-linear-to-r from-tiber-600 to-tiber-500 hover:from-tiber-700 hover:to-tiber-600"
                      >
                        <Wand2 className="w-4 h-4 mr-2" />
                        {t(
                          "documentView.aiImprove.modal.improveWithInstructions"
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {Modal}
      </div>
    </div>
  );
};
